/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from "@fluidframework/core-utils/internal";
import { BTree } from "@tylerbu/sorted-btree-es6";
import { findAncestor, findCommonAncestor, mintCommit, rebaseChange, } from "../core/index.js";
import { brand, fail, getOrCreate, mapIterable } from "../util/index.js";
import { SharedTreeBranch, getChangeReplaceType, onForkTransitive, } from "./branch.js";
import { decrementSequenceId, equalSequenceIds, maxSequenceId, minSequenceId, sequenceIdComparator, } from "./sequenceIdUtils.js";
import { createEmitter } from "../events/index.js";
import { TelemetryEventBatcher, measure, } from "@fluidframework/telemetry-utils/internal";
export const minimumPossibleSequenceNumber = brand(Number.MIN_SAFE_INTEGER);
const minimumPossibleSequenceId = {
    sequenceNumber: minimumPossibleSequenceNumber,
};
/**
 * Max number of telemetry log call that may be aggregated before being sent.
 */
const maxRebaseStatsAggregationCount = 1000;
/**
 * Represents a local branch of a document and interprets the effect on the document of adding sequenced changes,
 * which were based on a given session's branch, to the document history
 */
// TODO: Try to reduce this to a single type parameter
export class EditManager {
    /**
     * @param changeFamily - the change family of changes on the trunk and local branch
     * @param localSessionId - the id of the local session that will be used for local commits
     */
    constructor(changeFamily, localSessionId, mintRevisionTag, logger) {
        this.changeFamily = changeFamily;
        this.localSessionId = localSessionId;
        this.mintRevisionTag = mintRevisionTag;
        this._events = createEmitter();
        /**
         * Records extra data associated with commits in the {@link trunk}.
         * This does not include an entry for the {@link trunkBase}.
         */
        this.trunkMetadata = new Map();
        /**
         * A map from a sequence id to the commit in the {@link trunk} which has that sequence id.
         * This also includes an entry for the {@link trunkBase} which always has the lowest key in the map.
         */
        this.sequenceMap = new BTree(undefined, sequenceIdComparator);
        /**
         * Branches are maintained to represent the local change list that the issuing client had
         * at the time of submitting the latest known edit on the branch.
         * This means the head commit of each branch is always in its original (non-rebased) form.
         */
        this.peerLocalBranches = new Map();
        /**
         * Tracks where on the trunk all registered branches are based. Each key is the sequence id of a commit on
         * the trunk, and the value is the set of all branches who have that commit as their common ancestor with the trunk.
         *
         * @remarks
         * This does not include the local branch.
         */
        this.trunkBranches = new BTree(undefined, sequenceIdComparator);
        /**
         * The sequence number of the newest commit on the trunk that has been received by all peers.
         * Defaults to {@link minimumPossibleSequenceNumber} if no commits have been received.
         *
         * @remarks If there are more than one commit with the same sequence number we assume this refers to the last commit in the batch.
         */
        this.minimumSequenceNumber = minimumPossibleSequenceNumber;
        /**
         * The list of commits (from oldest to most recent) that are on the local branch but not on the trunk.
         * When a local commit is sequenced, the first commit in this list shifted onto the tip of the trunk.
         */
        this.localCommits = [];
        this.trunkBase = {
            revision: "root",
            change: changeFamily.rebaser.compose([]),
        };
        this.sequenceMap.set(minimumPossibleSequenceId, this.trunkBase);
        if (logger !== undefined) {
            this.telemetryEventBatcher = new TelemetryEventBatcher({
                eventName: "rebaseProcessing",
                category: "performance",
            }, logger, maxRebaseStatsAggregationCount);
        }
        this.trunk = new SharedTreeBranch(this.trunkBase, changeFamily, mintRevisionTag, this._events, this.telemetryEventBatcher);
        this.localBranch = new SharedTreeBranch(this.trunk.getHead(), changeFamily, mintRevisionTag, this._events, this.telemetryEventBatcher);
        this.localBranch.on("afterChange", (event) => {
            if (event.type === "append") {
                for (const commit of event.newCommits) {
                    this.localCommits.push(commit);
                }
            }
            else {
                this.localCommits.length = 0;
                findCommonAncestor([this.localBranch.getHead(), this.localCommits], this.trunk.getHead());
            }
        });
        // Track all forks of the local branch for purposes of trunk eviction. Unlike the local branch, they have
        // an unknown lifetime and rebase frequency, so we can not make any assumptions about which trunk commits
        // they require and therefore we monitor them explicitly.
        onForkTransitive(this.localBranch, (fork) => this.registerBranch(fork));
    }
    /**
     * Make the given branch known to the `EditManager`. The `EditManager` will ensure that all registered
     * branches remain usable even as the minimum sequence number advances.
     *
     * TODO#AB6926: Refactor local branch management into a separate class that encapsulates `trunkBranches` and everything
     * that touches it.
     * TODO#AB6925: Maintain the divergence point between each branch and the trunk so that we don't have to recompute
     * it so often.
     */
    registerBranch(branch) {
        this.trackBranch(branch);
        // Whenever the branch is rebased, update our record of its base trunk commit
        const offBeforeRebase = branch.on("beforeChange", (args) => {
            if (args.type === "replace" && getChangeReplaceType(args) === "rebase") {
                this.untrackBranch(branch);
            }
        });
        const offAfterRebase = branch.on("afterChange", (args) => {
            if (args.type === "replace" && getChangeReplaceType(args) === "rebase") {
                this.trackBranch(branch);
                this.trimTrunk();
            }
        });
        // When the branch is disposed, update our branch set and trim the trunk
        const offDispose = branch.on("dispose", () => {
            this.untrackBranch(branch);
            this.trimTrunk();
            offBeforeRebase();
            offAfterRebase();
            offDispose();
        });
    }
    trackBranch(b) {
        const trunkCommit = findCommonAncestor(this.trunk.getHead(), b.getHead()) ??
            fail("Expected branch to be related to trunk");
        const sequenceId = this.getCommitSequenceId(trunkCommit);
        const branches = getOrCreate(this.trunkBranches, sequenceId, () => new Set());
        assert(!branches.has(b), 0x670 /* Branch was registered more than once */);
        branches.add(b);
    }
    untrackBranch(b) {
        const trunkCommit = findCommonAncestor(this.trunk.getHead(), b.getHead()) ??
            fail("Expected branch to be related to trunk");
        const sequenceId = this.getCommitSequenceId(trunkCommit);
        const branches = this.trunkBranches.get(sequenceId) ?? fail("Expected branch to be tracked");
        assert(branches.delete(b), 0x671 /* Expected branch to be tracked */);
        if (branches.size === 0) {
            this.trunkBranches.delete(sequenceId);
        }
    }
    /**
     * Updates the `trunkBranches` map to reflect the fact that a new local commit was fast-forwarded to the trunk.
     * @param priorTrunkCommit - The trunk head prior to the fast-forward.
     * @param newId - The ID of the new trunk head.
     */
    fastForwardBranches(priorTrunkCommit, newId) {
        const currentId = this.getCommitSequenceId(priorTrunkCommit);
        const currentBranches = this.trunkBranches.get(currentId);
        if (currentBranches !== undefined) {
            const newBranches = getOrCreate(this.trunkBranches, newId, () => new Set());
            for (const branch of currentBranches) {
                if (branch.getHead() !== priorTrunkCommit) {
                    newBranches.add(branch);
                    currentBranches.delete(branch);
                }
            }
            if (currentBranches.size === 0) {
                this.trunkBranches.delete(currentId);
            }
        }
    }
    /**
     * Advances the minimum sequence number, and removes all commits from the trunk which lie outside the collaboration window,
     * if they are not retained by revertibles or local branches.
     * @param minimumSequenceNumber - the sequence number of the newest commit that all peers (including this one) have received and applied to their trunks.
     *
     * @remarks If there are more than one commit with the same sequence number we assume this refers to the last commit in the batch.
     */
    advanceMinimumSequenceNumber(minimumSequenceNumber) {
        if (minimumSequenceNumber === this.minimumSequenceNumber) {
            return;
        }
        assert(minimumSequenceNumber > this.minimumSequenceNumber, 0x476 /* number must be larger or equal to current minimumSequenceNumber. */);
        this.minimumSequenceNumber = minimumSequenceNumber;
        this.trimTrunk();
    }
    /**
     * Examines the latest known minimum sequence number and the trunk bases of any registered branches to determine
     * if any commits on the trunk are unreferenced and unneeded for future computation; those found are evicted from the trunk.
     * @returns the number of commits that were removed from the trunk
     */
    trimTrunk() {
        /** The sequence id of the oldest commit on the trunk that will be retained */
        let trunkTailSequenceId = {
            sequenceNumber: this.minimumSequenceNumber,
            indexInBatch: Number.POSITIVE_INFINITY,
        };
        // If there are any outstanding registered branches, get the one that is the oldest (has the "most behind" trunk base)
        const minimumBranchBaseSequenceId = this.trunkBranches.minKey();
        if (minimumBranchBaseSequenceId !== undefined) {
            // If that branch is behind the minimum sequence id, we only want to evict commits older than it,
            // even if those commits are behind the minimum sequence id
            const sequenceIdBeforeMinimumBranchBase = decrementSequenceId(minimumBranchBaseSequenceId);
            trunkTailSequenceId = minSequenceId(trunkTailSequenceId, sequenceIdBeforeMinimumBranchBase);
        }
        const [sequenceId, latestEvicted] = this.getClosestTrunkCommit(maxSequenceId(trunkTailSequenceId, this.sequenceMap.minKey() ?? minimumPossibleSequenceId));
        // Don't do any work if the commit found by the search is already the tail of the trunk
        if (latestEvicted !== this.trunkBase) {
            // The minimum sequence number informs us that all peer branches are at least caught up to the tail commit,
            // so rebase them accordingly. This is necessary to prevent peer branches from referencing any evicted commits.
            for (const [, branch] of this.peerLocalBranches) {
                branch.rebaseOnto(this.trunk, latestEvicted);
            }
            // This mutation is a performance hack. If commits are truly immutable, then changing the trunk's tail requires
            // regenerating the entire commit graph. Instead, we can simply chop off the tail like this if we're certain
            // that there are no outstanding references to any of the commits being removed (other than the references via
            // the trunk). The peer branches have been rebased to the head of the trunk, the local branch is already rebased
            // to the head of the trunk, and all other branches are tracked by `trunkBranches` and known to be ahead of or at
            // `newTrunkBase`. Therefore, no branches should have unique references to any of the commits being evicted here.
            // We mutate the most recent of the evicted commits to become the new trunk base. That way, any other commits that
            // have parent pointers to the latest evicted commit will stay linked, even though that it is no longer part of the trunk.
            const newTrunkBase = latestEvicted;
            // The metadata for new trunk base revision needs to be deleted before modifying it.
            this.trunkMetadata.delete(newTrunkBase.revision);
            // collect the revisions that will be trimmed to send as part of the branch trimmed event
            const trimmedRevisions = getPathFromBase(newTrunkBase, this.trunkBase).map((c) => c.revision);
            // Copying the revision of the old trunk base into the new trunk base means we don't need to write out the original
            // revision to summaries. All clients agree that the trunk base always has the same hardcoded revision.
            newTrunkBase.revision = this.trunkBase.revision;
            // Overwriting the change is not strictly necessary, but done here for consistency (so all trunk bases are deeply equal).
            newTrunkBase.change = this.trunkBase.change;
            // Dropping the parent field removes (transitively) all references to the evicted commits so they can be garbage collected.
            delete newTrunkBase.parent;
            this.trunkBase = newTrunkBase;
            // Update any state that is derived from trunk commits
            this.sequenceMap.editRange(minimumPossibleSequenceId, sequenceId, true, (s, { revision }) => {
                // Cleanup look-aside data for each evicted commit
                this.trunkMetadata.delete(revision);
                // Delete all evicted commits from `sequenceMap` except for the latest one, which is the new `trunkBase`
                if (equalSequenceIds(s, sequenceId)) {
                    assert(revision === newTrunkBase.revision, 0x729 /* Expected last evicted commit to be new trunk base */);
                }
                else {
                    return { delete: true };
                }
            });
            const trunkSize = getPathFromBase(this.trunk.getHead(), this.trunkBase).length;
            assert(this.sequenceMap.size === trunkSize + 1, 0x744 /* The size of the sequenceMap must have one element more than the trunk */);
            assert(this.trunkMetadata.size === trunkSize, 0x745 /* The size of the trunkMetadata must be the same as the trunk */);
            this._events.emit("ancestryTrimmed", trimmedRevisions);
        }
    }
    isEmpty() {
        return (this.trunk.getHead() === this.trunkBase &&
            this.peerLocalBranches.size === 0 &&
            this.localBranch.getHead() === this.trunk.getHead() &&
            this.minimumSequenceNumber === minimumPossibleSequenceNumber);
    }
    getSummaryData() {
        // The assert below is acceptable at present because summarization only ever occurs on a client with no
        // local/in-flight changes.
        // In the future we may wish to relax this constraint. For that to work, the current implementation of
        // `EditManager` would have to be amended in one of two ways:
        // A) Changes made by the local session should be represented by a branch in `EditManager.branches`.
        // B) The contents of such a branch should be computed on demand based on the trunk.
        // Note that option (A) would be a simple change to `addSequencedChange` whereas (B) would likely require
        // rebasing trunk changes over the inverse of trunk changes.
        assert(this.localBranch.getHead() === this.trunk.getHead(), 0x428 /* Clients with local changes cannot be used to generate summaries */);
        let oldestCommitInCollabWindow = this.getClosestTrunkCommit(this.minimumSequenceNumber)[1];
        assert(oldestCommitInCollabWindow.parent !== undefined ||
            oldestCommitInCollabWindow === this.trunkBase, 0x8c7 /* Expected oldest commit in collab window to have a parent or be the trunk base */);
        // Path construction is exclusive, so we need to use the parent of the oldest commit in the window if it exists
        oldestCommitInCollabWindow =
            oldestCommitInCollabWindow.parent ?? oldestCommitInCollabWindow;
        const trunk = getPathFromBase(this.trunk.getHead(), oldestCommitInCollabWindow).map((c) => {
            const metadata = this.trunkMetadata.get(c.revision) ?? fail("Expected metadata for trunk commit");
            const commit = {
                change: c.change,
                revision: c.revision,
                sequenceNumber: metadata.sequenceId.sequenceNumber,
                sessionId: metadata.sessionId,
            };
            if (metadata.sequenceId.indexInBatch !== undefined) {
                commit.indexInBatch = metadata.sequenceId.indexInBatch;
            }
            return commit;
        });
        const peerLocalBranches = new Map(mapIterable(this.peerLocalBranches.entries(), ([sessionId, branch]) => {
            const branchPath = [];
            const ancestor = findCommonAncestor([branch.getHead(), branchPath], this.trunk.getHead()) ??
                fail("Expected branch to be based on trunk");
            return [
                sessionId,
                {
                    base: ancestor.revision,
                    commits: branchPath.map((c) => {
                        const commit = {
                            change: c.change,
                            revision: c.revision,
                            sessionId,
                        };
                        return commit;
                    }),
                },
            ];
        }));
        return { trunk, peerLocalBranches };
    }
    loadSummaryData(data) {
        assert(this.isEmpty(), 0x68a /* Attempted to load from summary after edit manager was already mutated */);
        // Record the tags of each trunk commit as we generate the trunk so they can be looked up quickly
        // when hydrating the peer branches below
        const trunkRevisionCache = new Map();
        trunkRevisionCache.set(this.trunkBase.revision, this.trunkBase);
        this.trunk.setHead(data.trunk.reduce((base, c) => {
            const sequenceId = c.indexInBatch === undefined
                ? {
                    sequenceNumber: c.sequenceNumber,
                }
                : {
                    sequenceNumber: c.sequenceNumber,
                    indexInBatch: c.indexInBatch,
                };
            const commit = mintCommit(base, c);
            this.sequenceMap.set(sequenceId, commit);
            this.trunkMetadata.set(c.revision, {
                sequenceId,
                sessionId: c.sessionId,
            });
            trunkRevisionCache.set(c.revision, commit);
            return commit;
        }, this.trunkBase));
        this.localBranch.setHead(this.trunk.getHead());
        for (const [sessionId, branch] of data.peerLocalBranches) {
            const commit = trunkRevisionCache.get(branch.base) ??
                fail("Expected summary branch to be based off of a revision in the trunk");
            this.peerLocalBranches.set(sessionId, new SharedTreeBranch(branch.commits.reduce(mintCommit, commit), this.changeFamily, this.mintRevisionTag));
        }
    }
    getCommitSequenceId(commit) {
        return this.trunkMetadata.get(commit.revision)?.sequenceId ?? minimumPossibleSequenceId;
    }
    getTrunkChanges() {
        return this.getTrunkCommits().map((c) => c.change);
    }
    getTrunkCommits() {
        return getPathFromBase(this.trunk.getHead(), this.trunkBase);
    }
    getTrunkHead() {
        return this.trunk.getHead();
    }
    getLocalChanges() {
        return this.getLocalCommits().map((c) => c.change);
    }
    getLocalCommits() {
        return this.localCommits;
    }
    /**
     * @returns The length of the longest branch maintained by this EditManager.
     * This may be the length of a peer branch or the local branch.
     * The length is counted from the lowest common ancestor with the trunk such that a fully sequenced branch would
     * have length zero.
     */
    getLongestBranchLength() {
        let max = 0;
        const trunkHead = this.trunk.getHead();
        for (const branch of this.peerLocalBranches.values()) {
            const branchPath = getPathFromBase(branch.getHead(), trunkHead);
            if (branchPath.length > max) {
                max = branchPath.length;
            }
        }
        const localPath = getPathFromBase(this.localBranch.getHead(), trunkHead);
        return Math.max(max, localPath.length);
    }
    addSequencedChange(newCommit, sequenceNumber, referenceSequenceNumber) {
        assert(sequenceNumber > this.minimumSequenceNumber, 0x713 /* Expected change sequence number to exceed the last known minimum sequence number */);
        const commitsSequenceNumber = this.getBatch(sequenceNumber);
        const sequenceId = commitsSequenceNumber.length === 0
            ? {
                sequenceNumber,
            }
            : {
                sequenceNumber,
                indexInBatch: commitsSequenceNumber.length,
            };
        if (newCommit.sessionId === this.localSessionId) {
            const headTrunkCommit = this.trunk.getHead();
            const firstLocalCommit = this.localCommits.shift();
            assert(firstLocalCommit !== undefined, 0x6b5 /* Received a sequenced change from the local session despite having no local changes */);
            // The first local branch commit is already rebased over the trunk, so we can push it directly to the trunk.
            this.pushGraphCommitToTrunk(sequenceId, firstLocalCommit, this.localSessionId);
            this.fastForwardBranches(headTrunkCommit, sequenceId);
            return;
        }
        // Get the revision that the remote change is based on
        const [, baseRevisionInTrunk] = this.getClosestTrunkCommit(referenceSequenceNumber);
        // Rebase that branch over the part of the trunk up to the base revision
        // This will be a no-op if the sending client has not advanced since the last time we received an edit from it
        const peerLocalBranch = getOrCreate(this.peerLocalBranches, newCommit.sessionId, () => new SharedTreeBranch(baseRevisionInTrunk, this.changeFamily, this.mintRevisionTag));
        peerLocalBranch.rebaseOnto(this.trunk, baseRevisionInTrunk);
        if (peerLocalBranch.getHead() === this.trunk.getHead()) {
            // If the branch is fully caught up and empty after being rebased, then push to the trunk directly
            this.pushCommitToTrunk(sequenceId, newCommit);
            peerLocalBranch.setHead(this.trunk.getHead());
        }
        else {
            // Otherwise, rebase the change over the trunk and append it, and append the original change to the peer branch.
            const { duration, output: newChangeFullyRebased } = measure(() => rebaseChange(this.changeFamily.rebaser, newCommit, peerLocalBranch.getHead(), this.trunk.getHead(), this.mintRevisionTag));
            this.telemetryEventBatcher?.accumulateAndLog({
                duration,
                ...newChangeFullyRebased.telemetryProperties,
            });
            peerLocalBranch.apply(newCommit.change, newCommit.revision);
            this.pushCommitToTrunk(sequenceId, {
                ...newCommit,
                change: newChangeFullyRebased.change,
            });
        }
        this.localBranch.rebaseOnto(this.trunk);
    }
    findLocalCommit(revision) {
        const commits = [];
        const commit = findAncestor([this.localBranch.getHead(), commits], (c) => c.revision === revision);
        assert(commit !== undefined, 0x599 /* Expected local branch to contain revision */);
        return [commit, commits];
    }
    pushCommitToTrunk(sequenceId, commit, local = false) {
        const mintedCommit = mintCommit(this.trunk.getHead(), commit);
        this.pushGraphCommitToTrunk(sequenceId, mintedCommit, commit.sessionId);
    }
    pushGraphCommitToTrunk(sequenceId, graphCommit, sessionId) {
        this.trunk.setHead(graphCommit);
        const trunkHead = this.trunk.getHead();
        this.sequenceMap.set(sequenceId, trunkHead);
        this.trunkMetadata.set(trunkHead.revision, { sequenceId, sessionId });
    }
    getClosestTrunkCommit(searchBy) {
        const sequenceId = typeof searchBy === "number"
            ? {
                // This is to make sure that the correct commit is selected in this 2 scenarios:
                // 1) The commit is unique for that sequence number
                // 2) There are more than one commit for the same sequence number, in this case we need to select the last one.
                sequenceNumber: searchBy,
                indexInBatch: Number.POSITIVE_INFINITY,
            }
            : searchBy;
        const commit = this.sequenceMap.getPairOrNextLower(sequenceId);
        assert(commit !== undefined, 0x746 /* sequence id has been evicted */);
        return commit;
    }
    getBatch(sequenceNumber) {
        const startSequenceId = {
            sequenceNumber,
        };
        const endSequenceId = {
            sequenceNumber: brand(sequenceNumber + 1),
        };
        return this.sequenceMap.getRange(startSequenceId, endSequenceId, false);
    }
}
/**
 * @returns the path from the base of a branch to its head
 */
function getPathFromBase(branchHead, baseBranchHead) {
    const path = [];
    assert(findCommonAncestor([branchHead, path], baseBranchHead) !== undefined, 0x573 /* Expected branches to be related */);
    return path;
}
//# sourceMappingURL=editManager.js.map